import OrgUsersList from "../components/OrgUsersList";
import {
  GET_ORG_USERS_BY_ORGANIZATION_ID,
  useLazyGetOrgUsersByOrganizationId,
} from "../operations/queries/getOrgUsersByOrganizationId";
import { useAddOrgUser } from "../operations/mutations/addOrgUser";
import { useAddUserToOrganization } from "../operations/mutations/addUserToOrganization";
import { useUpdateOrgUser } from "../operations/mutations/updateOrgUser";
import { useRemoveUserFromOrganization } from "../operations/mutations/removeUserFromOrganization";
import { useResetOrgUserPassword } from "../operations/mutations/resetOrgUserPassword";
import { useEffect, useState } from "react";
import { useLazyGetUserByEmail } from "../operations/queries/getUserByEmail";
import { User } from "../models/User";
import { CalibrationCertificate } from "../models/CalibrationCertificate";
import { useToggleArchivedStatusOrgUser } from "../operations/mutations/toggleArchivedStatusOrgUser";
import { OrgUserFileInput } from "../models/globalTypes";
import { useOrganizationPolicies } from "../hooks/useOrganizationPolicies";
import { useLazyGetProjectsByOrganizationId } from "../operations/queries/getProjectsByOrganizationId";
import { useAppContext } from "../hooks/useAppContext";
import { useLazyGetAdminRolesByOrganizationId } from "../operations/queries/getAdminRolesByOrganizationId";
import { ProjectFilter } from "../components/OrgUsersList";

const OrgUsersListContainer = () => {
  const { currentOrganization } = useAppContext();
  const [user, setUser] = useState<User | undefined>();
  const [projectFilter, setProjectFilter] = useState<ProjectFilter>({ type: "all" });
  const { authorizedToManageAdminOrgUsers } = useOrganizationPolicies();

  const {
    getOrgUsersByOrganizationId: getOrgUsers,
    data: orgUsers,
    status: getOrgUsersStatus,
  } = useLazyGetOrgUsersByOrganizationId();
  const { getAdminRolesByOrganizationId: getAdminRoles, data: adminRoles } = useLazyGetAdminRolesByOrganizationId();
  const { mutate: addOrgUserMutate, status: addOrgUserStatus } = useAddOrgUser();
  const { mutate: updateOrgUserMutate, status: updateOrgUserStatus } = useUpdateOrgUser();
  const { mutate: removeUserFromOrganizationMutate, status: deleteOrgUserStatus } = useRemoveUserFromOrganization();
  const { mutate: resetPasswordMutate, status: resetPasswordStatus } = useResetOrgUserPassword();
  const { mutate: addOrgUserWithExistingUserMutate, status: addOrgUserWithExistingUserStatus } =
    useAddUserToOrganization();
  const { mutate: toggleArchivedStatusOrgUserMutate, status: toggleArchivedStatusOrgUserStatus } =
    useToggleArchivedStatusOrgUser();
  const { getUserByEmail: getUser, data: userConnection } = useLazyGetUserByEmail("network-only");
  const { getProjectsByOrganizationId: getProjects, data: projects } = useLazyGetProjectsByOrganizationId();

  useEffect(() => {
    if (currentOrganization) {
      getOrgUsers({
        variables: {
          organizationId: currentOrganization.id,
          projectId: projectFilter.type === "project" ? projectFilter.projectId : null,
          noProject: projectFilter.type === "noProject" ? true : undefined,
        },
      });
    }
  }, [getOrgUsers, currentOrganization, projectFilter]);

  useEffect(() => {
    if (currentOrganization) {
      getAdminRoles({ variables: { organizationId: currentOrganization.id } });
    }
  }, [currentOrganization, getAdminRoles]);

  useEffect(() => {
    if (currentOrganization) {
      getProjects({
        variables: { organizationId: currentOrganization.id },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id]);

  const handleAddOrgUser = (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    adminRoleId: string | null,
    phoneNumber: string,
    calibrationCertificate: CalibrationCertificate,
    orgUserFiles: OrgUserFileInput[],
    licenceId: string | null,
  ) => {
    if (currentOrganization) {
      addOrgUserMutate({
        refetchQueries: [
          { query: GET_ORG_USERS_BY_ORGANIZATION_ID, variables: { organizationId: currentOrganization.id } },
        ],
        variables: {
          firstName,
          lastName,
          email,
          password,
          adminRoleId,
          organizationId: currentOrganization.id,
          licenceId,
          phoneNumber,
          calibrationCertificate,
          orgUserFiles,
        },
      });
    }
  };

  const handleAddOrgUserWithExistingUser = (
    userId: string,
    adminRoleId: string | null,
    phoneNumber: string,
    calibrationCertificate: CalibrationCertificate,
    orgUserFiles: OrgUserFileInput[],
    licenceId: string | null,
  ) => {
    if (currentOrganization) {
      addOrgUserWithExistingUserMutate({
        refetchQueries: [
          { query: GET_ORG_USERS_BY_ORGANIZATION_ID, variables: { organizationId: currentOrganization.id } },
        ],
        variables: {
          userId,
          adminRoleId,
          organizationId: currentOrganization.id,
          licenceId,
          phoneNumber,
          calibrationCertificate,
          orgUserFiles,
        },
      });
    }
  };

  const handleEditOrgUser = (
    userId: string,
    firstName: string,
    lastName: string,
    adminRoleId: string | null,
    phoneNumber: string,
    licenceId: string | null,
    calibrationCertificate: CalibrationCertificate,
    newOrgUserFiles: OrgUserFileInput[],
    deletedOrgUserFileIds: string[],
  ) => {
    if (currentOrganization) {
      updateOrgUserMutate({
        variables: {
          organizationId: currentOrganization.id,
          userId,
          firstName,
          lastName,
          adminRoleId,
          phoneNumber,
          licenceId,
          calibrationCertificate,
          newOrgUserFiles,
          deletedOrgUserFileIds,
        },
      });
    }
  };

  const handleDeleteOrgUser = (userId: string) => {
    if (currentOrganization) {
      removeUserFromOrganizationMutate({
        refetchQueries: [
          { query: GET_ORG_USERS_BY_ORGANIZATION_ID, variables: { organizationId: currentOrganization.id } },
        ],
        variables: {
          userId,
          organizationId: currentOrganization.id,
        },
      });
    }
  };

  const handleResetPassword = (userId: string, newPassword: string) => {
    if (currentOrganization) {
      resetPasswordMutate({
        variables: {
          organizationId: currentOrganization.id,
          userId,
          newPassword,
        },
      });
    }
  };

  const handleSearchUser = (email: string) => {
    if (email) {
      getUser({ variables: { email } });
    }
  };

  useEffect(() => {
    setUser(userConnection?.userByEmail || undefined);
  }, [userConnection]);

  const handleSearchUserReset = () => {
    setUser(undefined);
  };

  const handleToggleArchivedStatusOrgUser = (id: string) => {
    if (currentOrganization) {
      toggleArchivedStatusOrgUserMutate({
        variables: {
          id,
          organizationId: currentOrganization.id,
        },
      });
    }
  };

  return (
    <OrgUsersList
      activeLicences={currentOrganization?.activeLicences}
      isOrgUsersLoading={getOrgUsersStatus.loading}
      orgUsers={orgUsers && orgUsers.orgUsersByOrganizationId}
      projects={projects && projects.projectsByOrganizationId?.items}
      onProjectFilter={setProjectFilter}
      adminRoles={adminRoles && adminRoles.adminRolesByOrganizationId}
      onAddOrgUser={handleAddOrgUser}
      addUserStatus={addOrgUserStatus}
      onAddOrgUserWithExistingUser={handleAddOrgUserWithExistingUser}
      addUserWithExistingUserStatus={addOrgUserWithExistingUserStatus}
      onEditOrgUser={handleEditOrgUser}
      editUserStatus={updateOrgUserStatus}
      onDeleteOrgUser={handleDeleteOrgUser}
      deleteUserStatus={deleteOrgUserStatus}
      onResetPassword={handleResetPassword}
      resetPasswordStatus={resetPasswordStatus}
      onSearchUser={handleSearchUser}
      onSearchUserReset={handleSearchUserReset}
      user={user}
      onToggleArchivedStatusOrgUser={handleToggleArchivedStatusOrgUser}
      toggleArchivedStatusOrgUserStatus={toggleArchivedStatusOrgUserStatus}
      canAssignAdminRole={authorizedToManageAdminOrgUsers}
    ></OrgUsersList>
  );
};

export default OrgUsersListContainer;
